import React, { useContext } from 'react';
import PropType from 'prop-types';
import Picture from '@deporvillage/picture-component';
import { Config, formatPrice, I18n } from '@deporvillage/utils-library-component';

import styles from './CartDisplay.module.scss';

const CartDisplayClick = {
    Item: 'item',
    Icon: 'icon',
    ViewCart: 'view',
    PlaceOrder: 'place-order',
    Component: 'component',
};

const CartDisplayScroll = {
    Component: 'component',
};

/* istanbul ignore next */
const scrollToTop = (elem) =>
    elem.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
    });

const CartDisplay = ({ items, amount, totalItems, isOpened, onClick, onScroll }) => {

    const { isMobile, lang } = useContext(Config)
    const cartTop = React.useRef(null);

    const handleClickItem = () => onClick && onClick(CartDisplayClick.Item);
    const handleClickIconCart = () => onClick && onClick(CartDisplayClick.Icon);
    const handleClickViewCart = () => onClick && onClick(CartDisplayClick.ViewCart);
    const handleClickPlaceOrder = () => onClick && onClick(CartDisplayClick.PlaceOrder);
    const handleScrollComponent = () => onScroll && onScroll(CartDisplayScroll.Component);

    React.useEffect(() => {
        if (totalItems && isOpened && cartTop.current) {
            scrollToTop(cartTop.current);
        }
    }, [totalItems, isOpened]);

    return (
        <div className={ styles['cart-component'] }>
            { totalItems ? (
                <a
                    className={ styles['cart-icon'] }
                    data-testid='cart-icon'
                    rel='nofollow'
                    href='/checkout/cart/'
                    onClick={ handleClickIconCart }
                >
                    <span className={`${styles['icon']} icon icon-cart`}>
                        <span className={ styles['icon-badge'] } data-testid='cart-items-count'>
                            { totalItems }
                        </span>
                    </span>
                    <span className={ styles['cart-icon-text'] }>
                        <I18n>cart_page.title.cart</I18n>
                    </span>
                </a>
            ) : (
                <div className={`${ styles['cart-icon'] } ${ styles['empty'] }`}>
                    <span className={`${styles['icon']} icon icon-cart`}>
                        { totalItems !== null ? (
                            <span className={ styles['icon-badge'] } data-testid='cart-items-count'>
                                { totalItems }
                            </span>
                        ) : null}
                    </span>
                    <span className={ styles['cart-icon-text'] }>
                        <I18n>cart_page.title.cart</I18n>
                    </span>
                </div>
            )}

            {(!isMobile && totalItems && items?.length) || isOpened ? (
                <div
                    className={`${styles['cart-content']} ${isOpened ? styles['is-opened'] : ''}`}
                    data-testid='cart-content'
                >
                    <div className={`${styles['cart-content-added-item']} ${isOpened ? styles['is-visible'] : ''}`}>
                        <span className={`${styles['icon-check']} icon icon-done`} />
                        <span className={styles['cart-content-added-text']}>
                            <I18n>cart_page.action.add_to_cart</I18n>
                        </span>
                    </div>
                    <div
                        className={styles['cart-content-top']}
                        ref={cartTop}
                        onScroll={handleScrollComponent}
                        id='cart-display-content'
                        data-testid='cart-content-top'
                    >
                        {items?.map((item) => (
                            <a
                                key={item.id}
                                data-testid={`${item.id}-cart-product`}
                                className={styles['list-item']}
                                href={item.url}
                                onClick={handleClickItem}
                            >
                                <Picture
                                    src={`product/${
                                        item.gallery_images.images[item.gallery_images.default]
                                    }`}
                                    name={item.title}
                                    width={70}
                                    height={70}
                                />

                                <div className={styles['list-item-content']}>
                                    <span className={styles['item-quantity']}>
                                        {item.quantity} x{' '}
                                    </span>
                                    <span className={styles['item-title']}>{item.title}</span>
                                    {item.variants.map((variant) => (
                                        <div key={variant.id} className={styles['text-variant']}>
                                            <I18n>cart_page.title.size</I18n>:{' '}
                                            {
                                                variant.items.find(
                                                    (itemVariant) =>
                                                        itemVariant.product_id === variant.selected,
                                                ).name
                                            }
                                        </div>
                                    ))}
                                </div>
                                <div
                                    className={`${styles['price']} ${
                                        item.default_variant.price.pvp !==
                                        item.default_variant.price.price
                                            ? styles['with-pvp']
                                            : ''
                                    }`}
                                >
                                    {item.default_variant.price.pvp !==
                                    item.default_variant.price.price ? (
                                        <del className={styles['price-pvpr']}>
                                            <I18n
                                                args={{
                                                    pvp: formatPrice(
                                                        item.variants.length
                                                            ? item.variants.reduce(
                                                                  (price, variant) =>
                                                                      price +
                                                                      variant.items[
                                                                          variant.items.findIndex(
                                                                              (i) =>
                                                                                  i.product_id ===
                                                                                  variant.selected,
                                                                          )
                                                                      ].price.pvp,
                                                                  0,
                                                                  lang,
                                                              )
                                                            : item.default_variant.price.pvp,
                                                        item.default_variant.price.currency_code,
                                                        lang,
                                                    ),
                                                }}
                                            >
                                                cart_page.pricing.pvp
                                            </I18n>
                                        </del>
                                    ) : null}

                                    {formatPrice(
                                        item.variants.length
                                            ? item.variants.reduce(
                                                  (price, variant) =>
                                                      price +
                                                      variant.items[
                                                          variant.items.findIndex(
                                                              (i) =>
                                                                  i.product_id === variant.selected,
                                                          )
                                                      ].price.price,
                                                  0,
                                                  lang,
                                              )
                                            : item.default_variant.price.price,
                                        item.default_variant.price.currency_code,
                                        lang,
                                    )}
                                </div>
                            </a>
                        ))}
                    </div>

                    <div className={styles['cart-content-bottom']}>
                        <p className={styles['cart-content-bottom-subtotal']}>
                            <small>
                                <I18n>cart_page.title.subtotal</I18n>
                            </small>
                            <strong className={styles['price']}>
                                {formatPrice(
                                    amount,
                                    items[0].default_variant.price.currency_code,
                                    lang,
                                )}
                            </strong>
                        </p>
                        <div className={styles['cart-content-bottom-actions']}>
                            <div
                                className={styles['button-wrapper']}
                                data-testid='view-cart-button'
                            >
                                <button
                                    onClick={handleClickViewCart}
                                    className={`${styles['cart-button']} ${styles['view-cart']}`}
                                >
                                    <I18n>cart_page.action.view_cart</I18n>
                                </button>
                            </div>
                            <div
                                className={styles['button-wrapper']}
                                data-testid='place-order-button'
                            >
                                <button
                                    onClick={handleClickPlaceOrder}
                                    className={`${styles['cart-button']} ${styles['place-order']}`}
                                >
                                    <i className={`${styles['icon']} icon icon-cart`} />
                                    <span>
                                        <I18n>cart_page.action.place_order</I18n>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

CartDisplay.propTypes = {
    amount: PropType.number,
    totalItems: PropType.number,
    items: PropType.array,
    isOpened: PropType.bool,
    onClick: PropType.func,
    onScroll: PropType.func,
};

export default CartDisplay;
export { CartDisplayClick, CartDisplayScroll };
